import { useState } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useConfig } from '../contexts/ConfigContext';
import { exportProjectToExcel } from '../services/excelExportService';
import { formatUSD, formatCOP } from '../utils/formatters';
import { Download, Trash2, Users, TrendingUp, Eye, PieChart } from 'lucide-react';
import styles from './ProjectPlannerView.module.css';
import { ResourceProjectionModal } from '../components/ResourceProjectionModal';
import { ResourceDetailModal } from '../components/ResourceDetailModal';
import { Resource } from '../types';

export function ProjectPlannerView() {
  const { state, removeResource, generateProjectionForResources, clear, setProjectName } = useProject();
  const { config } = useConfig();
  const [currentPage, setCurrentPage] = useState(1);
  const [isProjectionModalOpen, setIsProjectionModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const itemsPerPage = 10;

  // Calculate pagination
  const totalPages = Math.ceil(state.resources.length / itemsPerPage);
  const paginatedResources = state.resources.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleOpenProjection = (resource: Resource) => {
    setSelectedResource(resource);
    setIsProjectionModalOpen(true);
  };

  const handleOpenDetail = (resource: Resource) => {
    setSelectedResource(resource);
    setIsDetailModalOpen(true);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(prev => prev + 1);
  };

  const handleExportExcel = async () => {
    try {
      if (state.resources.length === 0) {
        alert("No hay recursos para exportar");
        return;
      }

      await exportProjectToExcel({
        project: {
          id: 'temp-id',
          name: state.projectName || 'Proyecto Sin Nombre',
          userId: 'current-user',
          resources: state.resources,
          createdAt: new Date(),
          updatedAt: new Date(),
          projection: null
        },
        config: config
      });
    } catch (error) {
      console.error(error);
      alert("Error al exportar Excel");
    }
  };



  // Calculate one-time costs for a resource
  const getOneTimeCosts = (resource: typeof state.resources[0]) => {
    return resource.additionalCosts.headhunter +
      resource.additionalCosts.hiring +
      resource.additionalCosts.safetyStudy +
      resource.additionalCosts.laptopRental;
  };

  return (
    <div className={styles.container}>

      {/* Header Section in Container */}
      <div className={styles.summaryContainer}>
        <div className={styles.summaryHeaderLeft}>
          <PieChart size={18} className={styles.titleIcon} />
          <h2 className={styles.summaryTitle}>Resumen del Proyecto</h2>
        </div>
        <button
          onClick={handleExportExcel}
          className={styles.exportBtn}
        >
          <Download size={16} />
          <span>Exportar Excel</span>
        </button>
      </div>

      {/* Independent Cards Grid */}
      <div className={styles.summaryGrid} style={{ marginBottom: '2rem' }}>
        {/* Card 1: Total Resources */}
        <div className={styles.summaryCard}>
          <div className={styles.cardBlur} />
          <div className={styles.cardHeader}>
            <h3>Total Recursos</h3>
            <Users size={20} className={styles.cardHeaderIcon} />
          </div>
          <p className={styles.summaryValue}>{state.resources.length}</p>
          <span className={styles.summaryLabel}>Activos en el proyecto</span>
        </div>

        {/* Card 2: Total CTC */}
        <div className={styles.summaryCard}>
          <div className={styles.cardBlur} />
          <div className={styles.cardHeader}>
            <h3>Total CTC Mensual</h3>
            <TrendingUp size={20} className={styles.cardHeaderIcon} />
          </div>
          <p className={styles.summaryValue}>
            {formatUSD(state.resources.reduce((sum, r) => sum + r.monthlyCTC.totalUSD, 0))}
          </p>
          <span className={styles.summaryLabel}>Promedio estimado</span>
        </div>

        {/* Card 3: Salary Types */}
        <div className={styles.summaryCard}>
          <div className={styles.cardBlur} />
          <div className={styles.cardHeader}>
            <h3>Tipos de Salario</h3>
            <PieChart size={20} className={styles.cardHeaderIcon} />
          </div>
          <p className={styles.summaryValue}>
            {state.resources.filter(r => r.salaryInfo.type === 'integral').length} / {state.resources.filter(r => r.salaryInfo.type === 'ordinary').length}
          </p>
          <span className={styles.summaryLabel}>Integral / Ordinario</span>
        </div>
      </div>

      {/* Resources Section with Table */}
      <div className={styles.resourceSection}>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nombre del Recurso</th>
                <th>Proyecto</th>
                <th className={styles.centerAlign}>Tipo de Salario</th>
                <th className={styles.rightAlign}>Salario Base (COP)</th>
                <th className={styles.rightAlign}>CTC Mensual (USD)</th>
                <th className={styles.actionsHeader}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {state.resources.length === 0 ? (
                <tr className={styles.emptyRow}>
                  <td colSpan={6}>
                    <div className={styles.emptyState}>
                      <Users size={48} />
                      <p>No hay recursos en este proyecto</p>
                      <span>Agrega nuevos recursos desde la Calculadora</span>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedResources.map((resource) => (
                  <tr key={resource.id}>
                    <td>
                      <span className={styles.resourceName}>{resource.name}</span>
                    </td>
                    <td>
                      <span className={styles.projectNameCell}>{state.projectName}</span>
                    </td>
                    <td className={styles.centerAlign}>
                      <span className={`${styles.resourceType} ${resource.salaryInfo.type === 'integral' ? styles.typeIntegral : styles.typeOrdinary}`}>
                        {resource.salaryInfo.type === 'integral' ? 'Integral' : 'Ordinario'}
                      </span>
                    </td>
                    <td className={`${styles.costCell} ${styles.rightAlign}`}>{formatCOP(resource.salaryInfo.baseSalary)}</td>
                    <td className={`${styles.costCell} ${styles.rightAlign}`}>{formatUSD(resource.monthlyCTC.totalUSD)}</td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleOpenDetail(resource)}
                          title="Ver Detalle Completo"
                        >
                          <Eye size={18} />
                        </button>
                        <button
                          className={styles.actionButton}
                          onClick={() => handleOpenProjection(resource)}
                          title="Ver Proyección 12 Meses"
                        >
                          <TrendingUp size={18} />
                        </button>
                        <button
                          className={`${styles.actionButton} ${styles.deleteButton}`}
                          onClick={() => removeResource(resource.id)}
                          title="Eliminar recurso"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className={styles.pagination}>
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={styles.pageButton}
            >
              Anterior
            </button>
            <span className={styles.pageInfo}>
              Página {totalPages === 0 ? 0 : currentPage} de {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages || totalPages === 0}
              className={styles.pageButton}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {isProjectionModalOpen && selectedResource && (
        <ResourceProjectionModal
          isOpen={isProjectionModalOpen}
          onClose={() => setIsProjectionModalOpen(false)}
          resource={selectedResource}
        />
      )}

      {isDetailModalOpen && selectedResource && (
        <ResourceDetailModal
          isOpen={isDetailModalOpen}
          onClose={() => setIsDetailModalOpen(false)}
          resource={selectedResource}
        />
      )}
    </div>
  );
}
